import { gql, useApolloClient, useQuery, useReactiveVar } from '@apollo/client';
import dynamic from 'next/dynamic';
import { Banner, Box, List, Paragraph, Text, TextButton } from '@zillow/constellation-10';
import { MESSAGE_LOAD_ERROR_TEXT, MESSAGE_TYPES, NewMessageNotificationBanner } from '@zg-rentals/satellite-components';
import { format, isSameDay, isThisYear, isToday, isYesterday } from 'date-fns';
import React, { useContext, useEffect, useRef, useState } from 'react';
import smoothscroll from 'smoothscroll-polyfill';
import {
  Folder,
  MessageList_FetchPaginatedMessagesDocument,
  RenterProfile_GetAttachmentsByConversationIdDocument,
  StatusSelector_GetBadgeCountsDocument,
  useMessageList_GetConversationQuery,
} from '../../types/GraphqlTypes';
import { InboxPageMobileView } from '../../types/InboxPageMobileView';
import { ModalType } from '../../types/ModalType';
import {
  inboxPagePreventMarkAsReadVariable,
  inboxPagePreventMessageListScrollVariable,
  shouldRefetchMessagesVariable,
} from '../../lib/apollo';
import useConversationActions from '../../hooks/useConversationActions';
import useDevices from '../../hooks/useDevices';
import useModal from '../../hooks/useModal';
import { AppContext } from '../../contexts/AppContextProvider';
import { InboxPageContext } from '../../contexts/InboxPageContextProvider';
import { isUserAuthError, logApolloError } from '../../utils/errorUtils';
import stringUtils from '../../utils/stringUtils';
import viewUtils from '../../utils/viewUtils';
import isValidListingAlias from '../../utils/isValidListingAlias';
import isValidConversationId from '../../utils/isValidConversationId';
import AddMessage from '../AddMessage';
import { AttachmentsForMessage } from '../MessageAttachments';
import MessageItem from '../MessageItem';
import DeleteAttachments from '../Modals/DeleteAttachments';
import RenterProfileChatBubble from '../RenterProfileChatBubble';
import EmptyContainer from './components/EmptyContainer';
import { MessageListContentsSkeleton } from './skeleton';
import useFeatureFlagsHelper from '../../hooks/useFeatureFlagsHelper';
import { appMarkerLogger, bootstrap } from '../../bootstrap';
import { scrollbarStyles } from '../../styles/scrollbar';

const logger = appMarkerLogger.child({ name: 'MessageList' });

const ImagesViewer = dynamic(() => import('../ImagesViewer'), {
  loading: () => null,
  ssr: false,
});

const POLL_INTERVAL_IN_SECONDS = 60;
const POLL_INTERVAL_IN_MS = POLL_INTERVAL_IN_SECONDS * 1000;
const DATE_FNS_FORMAT = 'EEEE, MMM do';
const DATE_FNS_FORMAT_WITH_YEAR = 'MMM do, yyyy';
const START_CONVERSATION_TEXT = 'This is the start of your conversation';

// Polyfill smooth scroll for safari :(
if (typeof window !== 'undefined' && window !== null) {
  smoothscroll.polyfill();
}

type OpenDeleteAttachmentsModalArgs = {
  attachments: AttachmentsForMessage;
  messageId: string;
};

type OpenImagesViewerArgs = {
  imageAttachments: AttachmentsForMessage;
  selectedImageIndex: number;
};

export type HandleOpenDeleteAttachmentsModal = ({ attachments, messageId }: OpenDeleteAttachmentsModalArgs) => void;
export type HandleOpenImagesViewer = ({ imageAttachments, selectedImageIndex }: OpenImagesViewerArgs) => void;

const MessageList = (): JSX.Element => {
  const { conversationId } = useContext(InboxPageContext);

  return conversationId ? <MessageListItems /> : <EmptyContainer showError={false} />;
};

export default MessageList;

const MessageListItems = (): JSX.Element => {
  const apolloClient = useApolloClient();
  const shouldRefetchMessages = useReactiveVar(shouldRefetchMessagesVariable);
  let resizeObserver: ResizeObserver | null = null;

  // Refs
  const addMessageContainerRef = useRef<HTMLDivElement>(null);
  const messageListContainerRef = useRef<HTMLDivElement>(null);
  const messageListEndRef = useRef<HTMLDivElement | null>(null);
  const currentConversationId = useRef<string | undefined>('');

  // State
  const [prevMessageCount, setPrevMessageCount] = useState<number | null>(null);
  const [showPaginationLoading, setShowPaginationLoading] = useState(false);
  const [showNewMessageNotification, setShowNewMessageNotification] = useState(false);
  const [prevFullMessageCount, setPrevFullMessageCount] = useState<number>(0);
  const [imagesViewerData, setImagesViewerData] = useState<OpenImagesViewerArgs | null>(null);
  const [deleteAttachmentsModalData, setDeleteAttachmentsModalData] = useState<OpenDeleteAttachmentsModalArgs | null>(
    null,
  );
  const [hasAuthError, setHasAuthError] = useState(false);
  const { setHasMessageListError } = useContext(AppContext);
  const { isDesktopView } = useDevices();
  const { conversationId, listingAlias, clientTimeZone, mobileView, folder } = useContext(InboxPageContext);
  const { activeModal, setActiveModal } = useModal();
  const { markConversationAsRead } = useConversationActions();
  const { data: inboxPageVariables } = useQuery(GET_INBOX_PAGE_VARIABLES, { fetchPolicy: 'cache-only' });
  const { enableWebsocketIntegration } = useFeatureFlagsHelper();

  // check if the user has scrolled to the bottom of the message list
  const isMessagesEndInViewportBeforePoll = viewUtils.isElementInViewport(messageListEndRef.current);

  const {
    data: messageListData,
    loading,
    error,
    fetchMore,
    refetch,
    startPolling,
    stopPolling,
  } = useMessageList_GetConversationQuery({
    variables: {
      conversationId: conversationId || '',
      listingAlias: listingAlias || '',
      clientTimeZone,
    },
    fetchPolicy: 'cache-first',
    pollInterval: enableWebsocketIntegration ? 0 : POLL_INTERVAL_IN_MS,
    ssr: false,
    onCompleted(data) {
      if (prevMessageCount === null) {
        setPrevMessageCount(Number(data.ZRM_getConversationById.pagination?.pageConversationCount));
        setPrevFullMessageCount(Number(data.ZRM_getConversationById.pagination?.queryFullMessageCount));
      }
      // Did conversation change
      if (currentConversationId.current != conversationId) {
        // Scroll to bottom
        // Do that here instead of on a useEffect so we only scroll after new data is finished loading
        setPrevMessageCount(Number(data.ZRM_getConversationById.pagination?.pageConversationCount));
        currentConversationId.current = conversationId;
        setTimeout(scrollToBottom, 500);
      } else {
        const currentFullMessageCount = Number(data.ZRM_getConversationById.pagination?.queryFullMessageCount);
        const { hasUnreadMessage } = data.ZRM_getConversationById;

        if (hasUnreadMessage) {
          const shouldScrollToMessagesEnd =
            isMessagesEndInViewportBeforePoll &&
            !showPaginationLoading &&
            !inboxPageVariables.inboxPagePreventMessageListScroll;

          const shouldShowNewMessageNotification =
            !isMessagesEndInViewportBeforePoll && !showNewMessageNotification && !showPaginationLoading;

          // If the user was already scrolled to the bottom before the new message came in,
          // auto scroll to the bottom to keep the new message in view
          if (shouldScrollToMessagesEnd) {
            setTimeout(scrollToBottom, 500);
          } else if (shouldShowNewMessageNotification) {
            // If the user was not scrolled to the bottom, show the new message notification banner
            setShowNewMessageNotification(true);
          }
        } else if (!inboxPageVariables.inboxPagePreventMessageListScroll && !showPaginationLoading) {
          // Scroll to bottom of message list when hasUnreadMessage is false
          // (i.e. sending a new message & polling for new messages)
          setTimeout(scrollToBottom, 500);
        }

        // Call ZRM_getAttachmentsByConversationId when detecting any of the new messages
        // contains any attachment
        if (currentFullMessageCount && prevFullMessageCount !== currentFullMessageCount) {
          setPrevFullMessageCount(currentFullMessageCount);
          const { conversation } = data.ZRM_getConversationById;
          const newMessageStartIndex = Math.max(0, conversation.length - 1);
          const newMessageEndIndex = Math.max(
            0,
            newMessageStartIndex - (currentFullMessageCount - prevFullMessageCount) + 1,
          );
          for (let i = newMessageStartIndex; i >= newMessageEndIndex; i--) {
            const { attachments } = conversation[i];
            if (attachments.length) {
              apolloClient.refetchQueries({
                include: [RenterProfile_GetAttachmentsByConversationIdDocument],
              });
              break;
            }
          }
        }
      }

      // Reset pagination loading messaging
      if (showPaginationLoading) setShowPaginationLoading(false);
      // Reset inboxPagePreventMessageListScroll variable
      if (inboxPageVariables.inboxPagePreventMessageListScroll) {
        inboxPagePreventMessageListScrollVariable(false);
      }
      // Update error state to false to allow other components to render
      setHasMessageListError(false);
      setHasAuthError(false);
    },
    onError(err) {
      if (isUserAuthError(err)) {
        bootstrap.monitor.count({ name: 'User not authorized to view conversation' });
        logger.info('User not authorized to view conversation - viewing authorization error message', {
          conversationId,
          listingAlias,
        });

        setHasMessageListError(true);
        setHasAuthError(true);
      } else {
        setHasMessageListError(true);
        setHasAuthError(false);
        logApolloError(err, {
          details: 'MessageList - Error fetching messages',
          conversationId: conversationId || '',
          listingAlias: listingAlias || '',
          clientTimeZone,
          isValidListingAlias: isValidListingAlias(listingAlias || ''),
          isValidConversationId: isValidConversationId(conversationId || ''),
        });
      }

      // In the future we might want to show offline status
    },
  });

  useEffect(() => {
    if (showPaginationLoading) setShowPaginationLoading(false);
    if (showNewMessageNotification) setShowNewMessageNotification(false);
    if (inboxPageVariables.inboxPagePreventMarkAsRead) inboxPagePreventMarkAsReadVariable(false);
  }, [conversationId]);

  useEffect(() => {
    /**
     * As the AddMessage text area grows in height, scroll MessageList
     * to the bottom so that the latest message is always in view
     */
    if (window.ResizeObserver && addMessageContainerRef.current) {
      resizeObserver = new ResizeObserver(() => {
        if (messageListContainerRef?.current) {
          messageListContainerRef.current.scrollTo(0, messageListContainerRef.current.scrollHeight);
        }
      });
      resizeObserver.observe(addMessageContainerRef.current);
    }

    // Disconnect on component unmount
    return () => {
      addMessageContainerRef?.current && resizeObserver?.unobserve(addMessageContainerRef.current);
      resizeObserver?.disconnect();
    };
  }, []);

  // refetches the message list when receiving a new conversation through websocket that matches the currently viewing one
  useEffect(() => {
    if (enableWebsocketIntegration && shouldRefetchMessages) {
      refetch();
      // Reset the reactive variable after refetching
      shouldRefetchMessagesVariable(false);
    }
  }, [refetch, enableWebsocketIntegration]);

  // If value of feature flag enableWebsocketIntegration changes, update the polling accordingly
  useEffect(() => {
    if (enableWebsocketIntegration) {
      stopPolling();
    } else {
      startPolling(POLL_INTERVAL_IN_MS);
    }
    // Cleanup function to stop polling when the component unmounts or the value changes
    return () => stopPolling();
  }, [enableWebsocketIntegration, startPolling, stopPolling, POLL_INTERVAL_IN_MS]);

  if ((!messageListData && error) || inboxPageVariables.inboxPageShowEmptyContainer) {
    return <EmptyContainer showError={!!error} hasAuthError={hasAuthError} />;
  }

  const markConversationAsReadAndUpdateBadgeCount = async () => {
    if (messageListData?.ZRM_getConversationById.conversation.length) {
      const messages = messageListData.ZRM_getConversationById.conversation;
      await markConversationAsRead(messages[messages.length - 1].messageDateMs);
    }

    // Refetch badge count if filters dropdown is open
    if (activeModal === ModalType.FiltersSelector) {
      await apolloClient.query({
        query: StatusSelector_GetBadgeCountsDocument,
        fetchPolicy: 'network-only',
      });
    }
  };

  function scrollToBottom() {
    if (messageListEndRef.current) {
      messageListEndRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
        inline: 'end',
      });
    }
    setShowNewMessageNotification(false);
    if (!inboxPageVariables.inboxPagePreventMarkAsRead && messageListData?.ZRM_getConversationById.hasUnreadMessage) {
      if (isDesktopView || mobileView === InboxPageMobileView.MessageList) {
        markConversationAsReadAndUpdateBadgeCount();
      }
    }
  }

  const handleLoadMoreMessages = async () => {
    setShowPaginationLoading(true);
    const response = await fetchMore({
      query: MessageList_FetchPaginatedMessagesDocument,
      variables: {
        conversationId: conversationId || '',
        listingAlias: listingAlias || '',
        paginationStartIndex: messageListData?.ZRM_getConversationById.conversation[0].messageId || null,
        clientTimeZone,
      },
    });
    setPrevMessageCount(Number(response.data.ZRM_getConversationById.pagination?.pageConversationCount));
  };

  const handleOpenImagesViewer: HandleOpenImagesViewer = ({ imageAttachments, selectedImageIndex }) => {
    setImagesViewerData({
      imageAttachments,
      selectedImageIndex,
    });
    setActiveModal(ModalType.ImagesViewer);
  };

  const handleCloseNewMessageNotification = () => {
    setShowNewMessageNotification(false);
  };

  const handleOpenDeleteAttachmentsModal: HandleOpenDeleteAttachmentsModal = ({ attachments, messageId }) => {
    setDeleteAttachmentsModalData({
      attachments,
      messageId,
    });
    setActiveModal(ModalType.DeleteAttachmentsModal);
  };

  let prevMessageDateMs: string;
  const conversationMessages = messageListData?.ZRM_getConversationById.conversation || [];
  const { hasQuickResponses, quickResponses, isConversationBlockedByZillow, pagination } =
    messageListData?.ZRM_getConversationById || {};
  const currentPageConversationCount = Number(pagination?.pageConversationCount) || null;
  const fullMessageCountNumber =
    Number(messageListData?.ZRM_getConversationById.pagination?.queryFullMessageCount) || 0;
  const showMoveToInboxBanner = folder === Folder.Spam && !isConversationBlockedByZillow;

  return (
    <>
      {activeModal === ModalType.ImagesViewer && (
        <ImagesViewer
          imageAttachments={imagesViewerData?.imageAttachments ?? []}
          selectedImageIndex={imagesViewerData?.selectedImageIndex || 0}
        />
      )}

      {activeModal === ModalType.DeleteAttachmentsModal && (
        <DeleteAttachments
          attachments={deleteAttachmentsModalData?.attachments || []}
          messageId={deleteAttachmentsModalData?.messageId || ''}
        />
      )}

      {showNewMessageNotification && (
        <NewMessageNotificationBanner onActionClick={scrollToBottom} onClose={handleCloseNewMessageNotification} />
      )}

      {showMoveToInboxBanner && (
        <Banner tone="info">To reply to this renter, tap Move to inbox to move the conversation to your inbox.</Banner>
      )}

      <Box
        ref={messageListContainerRef}
        css={{
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
          paddingLeft: 'default',
          paddingRight: 'tight',
          flex: 1,
          width: '100%',
          ...scrollbarStyles,
        }}
      >
        {!messageListData && loading ? (
          <MessageListContentsSkeleton />
        ) : (
          <>
            {showPaginationLoading && (
              <TextButton disabled css={{ margin: 'default' }}>
                Loading more messages...
              </TextButton>
            )}

            {!showPaginationLoading && currentPageConversationCount !== null && currentPageConversationCount >= 10 && (
              <TextButton onClick={handleLoadMoreMessages} css={{ margin: 'default' }}>
                Load more messages
              </TextButton>
            )}

            {!showPaginationLoading && currentPageConversationCount !== null && currentPageConversationCount < 10 && (
              <Paragraph
                textStyle="body-sm-bold"
                css={{ color: 'text.subtle', textAlign: 'center', marginTop: 'default', marginBottom: 'loose' }}
              >
                {START_CONVERSATION_TEXT}
              </Paragraph>
            )}

            <List.Root appearance="none" css={{ marginBottom: 'loose' }}>
              {conversationMessages.map((conversationMessage, idx) => {
                let timestamp;
                const isLastMessageInList = idx === conversationMessages.length - 1;
                const { attachments, message, messageDateMs, messageId } = conversationMessage;
                const isMessageSameDayAsPrevious = isSameDay(+messageDateMs, +prevMessageDateMs);
                const key = `${messageId}-${messageDateMs}`;
                prevMessageDateMs = messageDateMs;

                if (messageDateMs && !isMessageSameDayAsPrevious) {
                  if (isYesterday(+messageDateMs)) {
                    timestamp = 'Yesterday';
                  } else if (isToday(+messageDateMs)) {
                    timestamp = 'Today';
                  } else {
                    const fnsFormat = isThisYear(new Date(+messageDateMs))
                      ? DATE_FNS_FORMAT
                      : DATE_FNS_FORMAT_WITH_YEAR;
                    timestamp = format(new Date(+messageDateMs), fnsFormat);
                  }
                }

                // Edge case: message objects with missing message AND attachments fields - return "failed to load" item
                if (!message && !attachments) {
                  conversationMessage = {
                    ...conversationMessage,
                    message: MESSAGE_LOAD_ERROR_TEXT,
                    messageType: MESSAGE_TYPES.SYSTEM_MESSAGE.FAILED_TO_LOAD,
                    uiTreatmentType: MESSAGE_TYPES.LOW_PRIORITY,
                  };

                  return (
                    <MessageItem
                      isLastMessageInList={isLastMessageInList}
                      key={key}
                      message={conversationMessage}
                      openDeleteAttachmentsModal={handleOpenDeleteAttachmentsModal}
                      openImagesViewer={handleOpenImagesViewer}
                    />
                  );
                }

                // Edge case: message field with no text AND no attachments - do not render
                if (stringUtils.isEmptyString(message || '') && attachments.length === 0) {
                  return null;
                }

                return (
                  <React.Fragment key={key}>
                    {timestamp && (
                      <Text
                        asChild
                        textStyle="body-xs"
                        css={{
                          display: 'block',
                          color: 'text.subtle',
                          marginBlock: 'default',
                          textAlign: 'center',
                        }}
                      >
                        <time>{timestamp}</time>
                      </Text>
                    )}
                    {idx === 0 && (currentPageConversationCount || 0) < 10 && <RenterProfileChatBubble />}
                    <MessageItem
                      isLastMessageInList={isLastMessageInList}
                      message={conversationMessage}
                      openDeleteAttachmentsModal={handleOpenDeleteAttachmentsModal}
                      openImagesViewer={handleOpenImagesViewer}
                    />
                  </React.Fragment>
                );
              })}
            </List.Root>
          </>
        )}
        <Box ref={messageListEndRef} css={{ paddingBottom: 'loose' }} />
      </Box>

      <AddMessage
        ref={addMessageContainerRef}
        hasQuickResponses={hasQuickResponses}
        quickResponses={quickResponses}
        fullMessageCountNumber={fullMessageCountNumber}
      />
    </>
  );
};

const GET_INBOX_PAGE_VARIABLES = gql`
  query {
    inboxPagePreventMarkAsRead @client
    inboxPagePreventMessageListScroll @client
    inboxPageShowEmptyContainer @client
  }
`;
